import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import getConfig from 'next/config'
import styled from '@emotion/styled'
import cookies from 'js-cookie'
import { Alert, Button, space } from '@ticketswap/solar'
import { Apple } from '@ticketswap/comets'
import { AUTHENTICATION_STARTED, AUTHENTICATION_COMPLETED } from '../../lib/tracking-events'
import { track, setTrackingIdentity } from '../../utils'
import getViewer from '../../lib/get-viewer'
import { GENERATE_AUTH_TOKEN } from './generateAuthToken'
import { useViewer } from '../ViewerContext'
import { useScript } from '~/hooks/useScript'
import { useMutation, gql } from '@apollo/client'

const { CLIENT_ID, TOKEN_COOKIE_NAME } = getConfig().publicRuntimeConfig

const AUTHENTICATE_USER_WITH_APPLE = gql`
  mutation authenticateUserWithApple($input: AuthenticateAppleUserInput!) {
    appleAuth: authenticateUserWithApple(input: $input) {
      apiAccessToken {
        tokenString
        expiresAt
      }
      isNew
    }
  }
`

const StyledAlert = styled(Alert)`
  margin-bottom: ${space[8]};
`

const AppleIDButton = ({ t, onSignup, onLogin, children }) => {
  const [errorMessage, setErrorMessage] = useState(null)
  const [loadingApple, setLoadingApple] = useState(false)
  const { setViewer } = useViewer()
  const [appleAuth, setAppleAuth] = useState(null)
  const [loaded] = useScript(
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
  )
  const [generate, { client }] = useMutation(GENERATE_AUTH_TOKEN)
  const [authenticate] = useMutation(AUTHENTICATE_USER_WITH_APPLE)

  useEffect(() => {
    if (loaded && window.AppleID) {
      window.AppleID.auth.init({
        clientId: 'com.ticketswap.signinwithapplewebservice',
        scope: 'name email',
        redirectURI: `${window.location.protocol}//${window.location.host}`,
        usePopup: true,
      })

      setAppleAuth(window.AppleID.auth)
    }
  }, [loaded, appleAuth])

  const handleCompleted = async appleData => {
    const {
      data: { authenticateClientForToken },
    } = await generate({
      variables: {
        input: {
          clientId: CLIENT_ID,
        },
      },
    })

    const input = {
      token: authenticateClientForToken.token,
      appleAccessToken: appleData.authorization.id_token,
    }

    if (appleData.user) {
      input.firstName = appleData.user.name.firstName
      input.lastName = appleData.user.name.lastName
    }

    let data

    try {
      const {
        data: { appleAuth: appleAuthData },
      } = await authenticate({
        variables: { input },
      })
      data = appleAuthData
    } catch (error) {
      return handleError({ error: error.message })
    }
    const { tokenString } = data.apiAccessToken

    cookies.set(TOKEN_COOKIE_NAME, tokenString, {
      expires: 30,
      secure: true,
      sameSite: 'lax',
    })

    const user = await getViewer(client)
    setTrackingIdentity(user)

    if (data.isNew) {
      return onSignup(appleData.user.name)
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'apple',
      isNew: data.isNew,
    })

    await client.resetStore()
    setViewer(user)
    onLogin()
  }

  const handleError = ({ error }) => {
    setLoadingApple(false)

    if (error === 'popup_closed_by_user') return

    if (error === 'popup_blocked_by_browser') {
      setErrorMessage(t('common:generic.popupBlocked'))
    } else {
      setErrorMessage(t('common:generic.somethingWentWrong'))
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'apple',
      error,
    })
  }

  return (
    <>
      {errorMessage && <StyledAlert variant="error">{errorMessage}</StyledAlert>}
      <Button
        loading={loadingApple}
        disabled={!loaded}
        fullWidth
        variant="apple"
        leftAdornment={<Apple size={24} />}
        onClick={async () => {
          setErrorMessage(null)
          setLoadingApple(true)
          track(AUTHENTICATION_STARTED, {
            method: 'apple',
          })

          try {
            const data = await appleAuth.signIn()
            handleCompleted(data)
          } catch (error) {
            handleError(error)
          }
        }}
      >
        {children}
      </Button>
    </>
  )
}

AppleIDButton.propTypes = {
  onSignup: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
}

export default AppleIDButton
